export class CustomSelect {
    /**
     * @type {HTMLElement}
     * @private
     */
    _domElm;

    /**
     * @type {HTMLSelectElement}
     * @private
     */
    _select;

    /**
     * @type {HTMLElement}
     * @private
     */
    _customSelect;

    /**
     * @type {boolean}
     * @private
     */
    _isOpen = false;

    get domElm () {
        return this._domElm;
    }

    set domElm (value) {
        this._domElm = value;
    }

    get select () {
        return this._select;
    }

    set select (value) {
        this._select = value;
    }

    get customSelect () {
        return this._customSelect;
    }

    set customSelect (value) {
        this._customSelect = value;
    }

    get isOpen () {
        return this._isOpen;
    }

    set isOpen (value) {
        this._isOpen = value;
    }

    constructor (domElm) {
        this.domElm = domElm;
        this.select = this.domElm.querySelector('select');

        if (!this.select) return;

        // To avoid duplication
        if (this.select.dataset.initialized === 'true') return;

        this.select.dataset.initialized = 'true';

        this.init();
    }

    init () {
        this.prepareLabelText();
        this.createCustomSelect();
        this.setupEvents();
        this.checkInitialValue();
    }

    checkInitialValue () {
        // Si une option est déjà sélectionnée (pas l'option vide), ajouter has-value
        if (this.select.selectedIndex > 0 && this.select.value !== '') {
            this.domElm.classList.add('has-value');
        }
    }

    prepareLabelText () {
        // Extraire le texte du label et le mettre dans un span
        const labelText = this.getLabelText();
        if (labelText) {
            const span = document.createElement('span');
            span.className = 'select-label-text';
            span.textContent = labelText;

            // Vider le label et ajouter le span
            const selectWrap = this.domElm.querySelector('.wpcf7-form-control-wrap');
            this.domElm.innerHTML = '';
            this.domElm.appendChild(span);
            this.domElm.appendChild(selectWrap);
        }
    }

    getLabelText () {
        // Récupérer le texte du label (avant le span)
        const clone = this.domElm.cloneNode(true);
        const span = clone.querySelector('.wpcf7-form-control-wrap');
        if (span) span.remove();
        return clone.textContent.trim();
    }

    createCustomSelect () {
        // Créer le conteneur custom
        const wrapper = document.createElement('div');
        wrapper.className = 'custom-select-wrapper';

        // Créer le bouton qui affiche la valeur sélectionnée
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'custom-select-button';

        const selectedText = document.createElement('span');
        selectedText.className = 'custom-select-text';
        selectedText.textContent = this.select.options[this.select.selectedIndex]?.text || '';

        const arrow = document.createElement('span');
        arrow.className = 'custom-select-arrow';
        arrow.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="11.897" height="11.762" viewBox="0 0 11.897 11.762"><path d="M11.106,4.958,2.286.2A1.77,1.77,0,0,0,.776.141,1.182,1.182,0,0,0,0,1.19v9.516a1.182,1.182,0,0,0,.776,1.049,1.77,1.77,0,0,0,1.51-.059l8.82-4.758a1.15,1.15,0,0,0,.656-.99A1.15,1.15,0,0,0,11.106,4.958ZM2.94,8.484V3.421l4.7,2.528Z" transform="translate(11.897) rotate(90)" fill="currentColor"/></svg>';

        button.appendChild(selectedText);
        button.appendChild(arrow);

        // Créer la liste des options
        const dropdown = document.createElement('div');
        dropdown.className = 'custom-select-dropdown';

        Array.from(this.select.options).forEach((option, index) => {
            if (option.value === '') return; // Ignorer l'option vide

            const optionElm = document.createElement('div');
            optionElm.className = 'custom-select-option';
            optionElm.textContent = option.text;
            optionElm.dataset.value = option.value;
            optionElm.dataset.index = index;

            if (option.selected) {
                optionElm.classList.add('selected');
            }

            dropdown.appendChild(optionElm);
        });

        wrapper.appendChild(button);
        wrapper.appendChild(dropdown);

        // Masquer le select natif
        this.select.style.display = 'none';

        // Insérer le custom select après le select natif
        this.select.parentNode.insertBefore(wrapper, this.select.nextSibling);

        this.customSelect = wrapper;
    }

    setupEvents () {
        const button = this.customSelect.querySelector('.custom-select-button');
        const dropdown = this.customSelect.querySelector('.custom-select-dropdown');
        const options = dropdown.querySelectorAll('.custom-select-option');

        // Toggle dropdown au clic sur le bouton
        button.addEventListener('click', (e) => {
            e.preventDefault();
            this.toggle();
        });

        // Sélection d'une option
        options.forEach(option => {
            option.addEventListener('click', () => {
                this.selectOption(option);
            });

            option.addEventListener('mouseenter', () => {
                option.classList.add('hover');
            });

            option.addEventListener('mouseleave', () => {
                option.classList.remove('hover');
            });
        });

        // Fermer au clic à l'extérieur
        document.addEventListener('click', (e) => {
            if (!this.customSelect.contains(e.target)) {
                this.close();
            }
        });
    }

    toggle () {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    open () {
        this.isOpen = true;
        this.customSelect.classList.add('is-open');
    }

    close () {
        this.isOpen = false;
        this.customSelect.classList.remove('is-open');
    }

    selectOption (optionElm) {
        const index = optionElm.dataset.index;
        const text = optionElm.textContent;

        // Mettre à jour le select natif
        this.select.selectedIndex = index;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));

        // Mettre à jour l'affichage
        const selectedText = this.customSelect.querySelector('.custom-select-text');
        selectedText.textContent = text;

        // Mettre à jour les classes selected
        this.customSelect.querySelectorAll('.custom-select-option').forEach(opt => {
            opt.classList.remove('selected');
        });
        optionElm.classList.add('selected');

        // Ajouter la classe has-value au label pour faire remonter le texte
        this.domElm.classList.add('has-value');

        this.close();
    }
}
